import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import profilepic from "../../assests/modalpic.png";
import userService from "../../api/services/userService"; // 👈 import your API service

const tabs = [
  { key: "profile", label: "Profile" },
  { key: "send", label: "Send Request" },
  { key: "received", label: "Got Request" },
  { key: "favourites", label: "Favourites" },
  { key: "blocked", label: "Block User" },
  // { key: "packages", label: "Purchase Packages" },
  { key: "documents", label: "Documents" },
];

const SectionCard = ({ title, children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-5 ${className}`}>
    <h3 className="text-2xl font-bold text-slate-800 mb-4">{title}</h3>
    {children}
  </div>
);

const KV = ({ label, value }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-[15px] font-semibold text-slate-800">{label}</span>
    <span className="text-[15px] text-slate-500">{value ?? "--"}</span>
  </div>
);

const UserDetails = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const [active, setActive] = useState("profile");
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [user, setUser] = useState(null);

  // Fetch user details from API
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await userService.detailsUser(id);
        setUser(res.details);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, [id]);

  const tabButtonClasses = (key) =>
    `whitespace-nowrap px-6 py-3 rounded-2xl text-sm font-semibold transition
     ${active === key
       ? "bg-red-500 text-white shadow-[0_10px_24px_rgba(239,68,68,0.45)]"
       : "text-slate-700 hover:text-red-500"}`;

  const ProfileTab = useMemo(
    () =>
      user && (
        <div className="bg-white rounded-3xl shadow-md border border-slate-100">
          <div className="p-5 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left: Avatar + Verify */}
              <div className="lg:col-span-4">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col items-center">
                  {user.imageUrl ? (
                    <img
                      src={user.imageUrl}
                      alt="User"
                      className="w-56 h-56 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-56 h-56 rounded-full bg-slate-200 flex items-center justify-center text-slate-400 text-6xl select-none">
                      <span className="translate-y-1">👤</span>
                    </div>
                  )}

                  <button
                    onClick={() => setVerifyOpen(true)}
                    className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-500 text-white font-semibold shadow-[0_10px_24px_rgba(239,68,68,0.45)] hover:bg-red-600"
                  >
                    Verify Profile
                    <FaCheckCircle className="opacity-90" />
                  </button>
                </div>
              </div>

              {/* Right: scrollable detail cards */}
              <div className="lg:col-span-8">
                <div className="max-h-[520px] overflow-y-auto pr-1 lg:pr-2 custom-scroll">
                  <div className="grid md:grid-cols-2 gap-5">
                    <SectionCard title="Personal Details">
                      <KV label="Marital Status" value={user.maritalStatus} />
                      <KV label="Height" value={user.height} />
                      <KV label="Age" value={user.age} />
                      <KV label="Languages" value={user.languages} />
                      <KV label="Diet" value={user.diet} />
                      <KV label="Children" value={user.childerns === 0 ? "Does not have Childern" : "Have Childern"} />
                    </SectionCard>

                    <SectionCard title="Education & Career Details">
                      <KV label="Education" value={user.education} />
                      <KV label="Occupation" value={user.occupation} />
                      <KV label="Monthly Income" value={user.monthlyIncome} />
                      <KV label="Career Status" value={user.careerStatus} />
                    </SectionCard>

                    <SectionCard title="Address Details" className="md:col-span-2">
                      <KV label="Address" value={user.address} />
                      <KV label="Grew Up" value={user.grewUp} />
                      <KV label="Residential" value={user.residential} />
                    </SectionCard>
                  </div>

                  {/* Basic Details full width row */}
                  <div className="mt-5">
                    <SectionCard title="Basic Details">
                      <div className="grid sm:grid-cols-2 gap-x-8">
                        <KV label="User ID" value={`#${user.id}`} />
                        <KV label="Name" value={`${user.firstName} ${user.lastName}`} />

                        <KV label="Religion" value={user.religion} />
                        <KV label="Sect" value={user.sect} />
                        <KV label="Parenting" value={user.parenting} />
                        <KV label="Marriage Plan" value={user.marriage} />
                        <KV label="Smoking" value={user.smoking} />
                        <KV label="Alcohol" value={user.alcohol} />
                      </div>
                    </SectionCard>
                  </div>

                  {/* About Me */}
                  <div className="mt-5">
                    <SectionCard title="About Me">
                      <p className="text-slate-600">{user.aboutMe}</p>
                    </SectionCard>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Verify Modal */}
          {verifyOpen && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
              <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl relative">
                <button
                  className="absolute top-3 right-3 text-slate-500 hover:text-slate-800"
                  onClick={() => setVerifyOpen(false)}
                >
                  ✕
                </button>
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4">Verify User Profile Pic</h2>
                  <div className="w-full flex items-center justify-center mb-6">
                    <img
                      src={user.imageUrl || profilepic}
                      alt="profile"
                      className="rounded-lg max-h-72 object-contain"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setVerifyOpen(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
                      Approve
                    </button>
                    <button className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ),
    [user, verifyOpen]
  );

  // Other tabs (placeholders remain same)
  const TabShell = ({ children }) => (
    <div className="bg-white rounded-3xl shadow-md border border-slate-100 p-6">{children}</div>
  );

  const SendRequestTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">Send Request</h3>
      <p className="text-slate-500">Sent requests will appear here.</p>
    </TabShell>
  );

  const GotRequestTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">Got Request</h3>
      <p className="text-slate-500">Received requests will appear here</p>
    </TabShell>
  );

  const FavouritesTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">Favourites</h3>
      <p className="text-slate-500">Favourite users will appear here</p>
    </TabShell>
  );

  const BlockedTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">Block User</h3>
      <p className="text-slate-500">Blocked users will appear here</p>
    </TabShell>
  );

  // const PackagesTab = (
  //   <TabShell>
  //     <h3 className="text-xl font-bold text-slate-800 mb-4">Purchase Packages</h3>
  //     <p className="text-slate-500">Packages purchased by user will show here</p>
  //   </TabShell>
  // );

  const DocumentsTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">Documents</h3>
      <p className="text-slate-500">User documents list.</p>
    </TabShell>
  );

  const renderTab = () => {
    switch (active) {
      case "profile": return ProfileTab;
      case "send": return SendRequestTab;
      case "received": return GotRequestTab;
      case "favourites": return FavouritesTab;
      case "blocked": return BlockedTab;
      // case "packages": return PackagesTab;
      case "documents": return DocumentsTab;
      default: return ProfileTab;
    }
  };

  return (
    <div className="p-4 md:p-6 w-full">
      {/* Tabs container */}
      <div className="bg-white rounded-3xl shadow-md border border-slate-100">
        <div className="px-4 md:px-6 pt-4">
          <div className="flex items-center gap-3 overflow-x-auto no-scrollbar pb-3">
            {tabs.map((t) => (
              <button
                key={t.key}
                className={tabButtonClasses(t.key)}
                onClick={() => setActive(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-px bg-slate-100" />
      </div>

      {/* Tab content */}
      <div className="mt-4">
        {renderTab()}
      </div>
    </div>
  );
};

export default UserDetails;
